import {
  BarChart2,
  CalendarDays,
  BookMarked,
  Columns2,
  CheckSquare,
  FileSignature,
  Kanban,
  MessageCircle,
  Shield,
  Sparkles,
  StickyNote,
  Table2,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * The single source of truth for "what apps exist".
 *
 * Before this module the list lived privately inside TopNav, and every other
 * surface that needed to know which app a URL belongs to (nav highlight, page
 * title, later: workspace pane chrome and note windows) had to re-derive it.
 * See docs/MULTI_APP_WORKSPACE.md §6.
 */

export const APP_KEYS = [
  'safezone',
  'notes',
  'todos',
  'page_app',
  'finance',
  'flow',
  'conversations',
  'doc',
  'contacts',
  'calendar',
  'tables',
  'ai',
  'workspace',
] as const;

export type AppKey = (typeof APP_KEYS)[number];

export interface AppDef {
  key: AppKey;
  /** i18n key, always `apps.<key>` — spelled out so it stays greppable. */
  titleKey: string;
  /** Where the launcher sends you. */
  path: string;
  /**
   * Route prefixes that mean "you are in this app". A prefix matches the path
   * itself or anything below it, so '/notes' covers '/notes/42' but not
   * '/notestaking'. Longest match wins.
   */
  match: string[];
  icon: LucideIcon;
  color: string;
  available: boolean;
  /** May this app be rendered inside a workspace pane? (Phase 2) */
  paneable: boolean;
}

/** Route of the split-screen workspace. */
export const WORKSPACE_PATH = '/workspace';

export const APP_REGISTRY: AppDef[] = [
  {
    key: 'safezone',
    titleKey: 'apps.safezone',
    path: '/safezone/passwords',
    match: ['/safezone'],
    icon: Shield,
    color: 'text-primary',
    available: true,
    paneable: true,
  },
  {
    key: 'notes',
    titleKey: 'apps.notes',
    path: '/notes',
    match: ['/notes'],
    icon: StickyNote,
    color: 'text-warning',
    available: true,
    paneable: true,
  },
  {
    key: 'todos',
    titleKey: 'apps.todos',
    path: '/todos',
    match: ['/todos'],
    icon: CheckSquare,
    color: 'text-warning',
    available: true,
    paneable: true,
  },
  {
    key: 'page_app',
    titleKey: 'apps.page_app',
    // The drive. /explorer still matches: old links redirect from there.
    path: '/pages',
    match: ['/explorer', '/pages'],
    icon: BookMarked,
    color: 'text-primary',
    available: true,
    paneable: true,
  },
  {
    key: 'finance',
    titleKey: 'apps.finance',
    path: '/finance',
    match: ['/finance'],
    icon: BarChart2,
    color: 'text-success',
    available: true,
    paneable: true,
  },
  {
    key: 'flow',
    titleKey: 'apps.flow',
    path: '/flow',
    match: ['/flow'],
    icon: Kanban,
    color: 'text-primary',
    available: true,
    paneable: true,
  },
  {
    key: 'conversations',
    titleKey: 'apps.conversations',
    path: '/conversations',
    match: ['/conversations'],
    icon: MessageCircle,
    color: 'text-primary',
    available: true,
    paneable: true,
  },
  {
    key: 'doc',
    titleKey: 'apps.doc',
    path: '/doc',
    match: ['/doc'],
    icon: FileSignature,
    color: 'text-primary',
    available: true,
    paneable: true,
  },
  {
    key: 'contacts',
    titleKey: 'apps.contacts',
    path: '/contacts',
    match: ['/contacts'],
    icon: Users,
    color: 'text-secondary',
    available: true,
    paneable: true,
  },
  {
    key: 'calendar',
    titleKey: 'apps.calendar',
    path: '/calendar',
    match: ['/calendar'],
    icon: CalendarDays,
    color: 'text-primary',
    available: true,
    paneable: true,
  },
  {
    key: 'tables',
    titleKey: 'apps.tables',
    path: '/tables',
    match: ['/tables'],
    icon: Table2,
    color: 'text-success',
    available: true,
    paneable: true,
  },
  {
    key: 'workspace',
    titleKey: 'apps.workspace',
    path: '/workspace',
    match: [WORKSPACE_PATH],
    icon: Columns2,
    color: 'text-secondary',
    available: true,
    // No workspace inside a workspace.
    paneable: false,
  },
  {
    key: 'ai',
    titleKey: 'apps.ai',
    path: '/ai',
    match: ['/ai'],
    icon: Sparkles,
    color: 'text-primary',
    available: true,
    paneable: true,
  },
];

const APP_BY_KEY: Record<AppKey, AppDef> = APP_REGISTRY.reduce(
  (acc, app) => ({ ...acc, [app.key]: app }),
  {} as Record<AppKey, AppDef>,
);

export function getApp(key: AppKey): AppDef {
  return APP_BY_KEY[key];
}

/**
 * Which app does this path belong to? `null` for Home, About, auth and profile
 * pages — they are platform surfaces, not apps.
 */
export function resolveAppFromPath(pathname: string): AppKey | null {
  // Callers pass live locations, which carry a query and sometimes a hash
  // ('/notes?tab=trash'). Match on the path only.
  const path = pathname.split(/[?#]/)[0];

  let bestKey: AppKey | null = null;
  let bestLength = -1;

  for (const app of APP_REGISTRY) {
    for (const prefix of app.match) {
      const hit = path === prefix || path.startsWith(`${prefix}/`);
      if (hit && prefix.length > bestLength) {
        bestKey = app.key;
        bestLength = prefix.length;
      }
    }
  }

  return bestKey;
}
